/* Impact adapter: confirm fragrance additions without opening the cart drawer. */
(() => {
  if (window.pwFragranceShopping) return;
  window.pwFragranceShopping = true;
  const pending = new WeakSet();
  let queue = Promise.resolve();
  let toastTimer;
  let activeToast;
  let activeInlineToast;
  let toastAnchor;
  let positionFrame;
  let activeDetail;
  const pendingDetails = new Set();
  const root = () => window.Shopify?.routes?.root || '/';
  // Read only text from the existing translated description: never copy its CSS/scripts.
  function fillTranslatedDetail(dialog) {
    const source = dialog.querySelector('[data-pw-detail-source]')?.content;
    const target = dialog.querySelector('[data-pw-detail-description]');
    if (!source || !target) return;
    const textElement = (tag, text, parent) => {
      const element = document.createElement(tag);
      element.textContent = text;
      parent.append(element);
      return element;
    };
    const intro = source.querySelector('.pw-section .pw-text');
    if (intro) intro.querySelectorAll('p').forEach(p => textElement('p', p.textContent, target));
    source.querySelectorAll('.pw-scent').forEach(scent => {
      const section = textElement('section', '', target);
      section.className = 'pw-detail__section';
      const heading = scent.querySelector('.pw-scent-title');
      if (heading) textElement('h3', heading.textContent, section);
      const tagline = scent.querySelector('.pw-scent-line');
      if (tagline) textElement('p', tagline.textContent, section);
      const list = textElement('dl', '', section);
      list.className = 'pw-detail__notes';
      scent.querySelectorAll('.pw-scent-row').forEach(row => {
        const item = textElement('div', '', list);
        textElement('dt', row.querySelector('.pw-scent-note')?.textContent || '', item);
        const values = textElement('dd', '', item);
        const chips = textElement('div', '', values);
        chips.className = 'pw-detail__chips';
        (row.querySelector('.pw-scent-desc')?.textContent || '').split(',').filter(Boolean).forEach(note => textElement('span', note.trim(), chips));
      });
    });
    if (!target.textContent.trim()) {
      // Plain paragraphs remain a usable fallback if description markup changes.
      source.querySelectorAll('p').forEach(p => textElement('p', p.textContent, target));
    }
    dialog.querySelector('[data-pw-detail-source]').remove();
  }
  function openDetail(link) {
    const card = link.closest('.pw-fragrances__card');
    const template = card?.querySelector('[data-pw-fragrance-detail]');
    if (!template || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return false;
    if (activeDetail?.open) return true;
    hideToast();
    const dialog = document.createElement('dialog');
    dialog.className = 'pw-fragrance-detail';
    dialog.append(template.content.cloneNode(true));
    dialog.setAttribute('aria-labelledby', dialog.querySelector('[data-pw-detail-title]').id);
    fillTranslatedDetail(dialog);
    const form = dialog.querySelector('[data-pw-detail-form]');
    const variantId = form.elements.namedItem('id').value;
    form.pwDetailContext = { dialog, link, shop: card.closest('[data-pw-fragrance-shop]'), anchor: card.querySelector('.pw-fragrances__add') || link };
    const submit = form.querySelector('[type="submit"]');
    submit.dataset.available = String(!submit.disabled);
    if (pendingDetails.has(variantId)) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
    const scroll = { left: window.scrollX, top: window.scrollY };
    dialog.addEventListener('close', () => {
      if (activeInlineToast && dialog.contains(activeInlineToast)) hideToast();
      dialog.remove();
      if (activeDetail !== dialog) return;
      activeDetail = null;
      document.documentElement.classList.remove('pw-fragrance-dialog-open');
      window.scrollTo({ ...scroll, behavior: 'instant' });
      if (link.isConnected) link.focus({ preventScroll: true });
      queueToastPosition();
    }, { once: true });
    dialog.addEventListener('click', event => {
      if (event.target.closest('[data-pw-detail-close]')) dialog.close();
      const thumbnail = event.target.closest('[data-pw-detail-photo]');
      if (thumbnail) {
        const photo = dialog.querySelector('[data-pw-detail-image]');
        photo.removeAttribute('srcset');
        photo.src = thumbnail.dataset.src;
        dialog.querySelectorAll('[data-pw-detail-photo]').forEach(button => button.setAttribute('aria-pressed', String(button === thumbnail)));
      }
    });
    let backdropPointer = false;
    dialog.addEventListener('pointerdown', event => { backdropPointer = event.target === dialog; });
    dialog.addEventListener('pointerup', event => { if (backdropPointer && event.target === dialog) dialog.close(); backdropPointer = false; });
    document.body.append(dialog);
    activeDetail = dialog;
    document.documentElement.classList.add('pw-fragrance-dialog-open');
    dialog.showModal();
    dialog.querySelector('[data-pw-detail-close]').focus({ preventScroll: true });
    return true;
  }
  function addFailure(shop, message, anchor, detail) {
    const dialog = currentDetailFor(detail);
    if (dialog?.open && dialog.isConnected) {
      const error = dialog.querySelector('[data-pw-detail-error]');
      error.textContent = message;
      error.hidden = false;
    } else notify(shop, message, true, anchor);
  }
  function currentDetailFor(detail) {
    if (!detail) return null;
    const id = detail.dialog.querySelector('[data-pw-detail-form]')?.elements.namedItem('id').value;
    const activeId = activeDetail?.querySelector('[data-pw-detail-form]')?.elements.namedItem('id').value;
    return id === activeId ? activeDetail : detail.dialog;
  }
  function hideToast() {
    clearTimeout(toastTimer);
    cancelAnimationFrame(positionFrame);
    if (activeToast?.contains(document.activeElement) && toastAnchor?.isConnected) toastAnchor.focus({ preventScroll: true });
    if (activeToast) activeToast.hidden = true;
    if (activeInlineToast) activeInlineToast.hidden = true;
    activeInlineToast = null;
    activeToast = toastAnchor = null;
  }
  function positionToast() {
    if (!activeToast?.isConnected || activeToast.hidden) return;
    const viewport = window.visualViewport;
    const left = (viewport?.offsetLeft || 0) + 12;
    const top = (viewport?.offsetTop || 0) + 12;
    const width = (viewport?.width || document.documentElement.clientWidth) - 24;
    const bottom = top + (viewport?.height || window.innerHeight) - 24;
    activeToast.style.maxWidth = `${Math.max(0, width)}px`;
    const box = activeToast.getBoundingClientRect();
    const anchor = toastAnchor?.isConnected && toastAnchor.getBoundingClientRect();
    // Keep confirmation next to its button, including after scrolling or resizing.
    let x = anchor ? anchor.left + anchor.width / 2 - box.width / 2 : left + (width - box.width) / 2;
    let y = anchor ? anchor.top - box.height - 10 : bottom - box.height;
    if (anchor && y < top && anchor.bottom + 10 + box.height <= bottom) y = anchor.bottom + 10;
    activeToast.style.left = `${Math.max(left, Math.min(x, left + width - box.width))}px`;
    activeToast.style.top = `${Math.max(top, Math.min(y, bottom - box.height))}px`;
  }
  function queueToastPosition() {
    if (!activeToast) return;
    cancelAnimationFrame(positionFrame);
    positionFrame = requestAnimationFrame(positionToast);
  }
  window.addEventListener('scroll', queueToastPosition, { passive: true, capture: true });
  window.addEventListener('resize', queueToastPosition, { passive: true });
  window.visualViewport?.addEventListener('resize', queueToastPosition, { passive: true });
  window.visualViewport?.addEventListener('scroll', queueToastPosition, { passive: true });
  function notify(shop, message, error = false, anchor = null) {
    const toast = shop.querySelector('[data-pw-fragrance-toast]');
    if (!toast) return;
    hideToast();
    if (activeDetail?.open) {
      activeInlineToast = activeDetail.querySelector('[data-pw-detail-feedback]');
      activeInlineToast.textContent = message;
      activeInlineToast.hidden = false;
      if (!error) toastTimer = setTimeout(hideToast, 3200);
      return;
    }
    activeToast = toast;
    toastAnchor = anchor;
    toast.hidden = false;
    toast.querySelector('[data-pw-fragrance-message]').textContent = message;
    positionToast();
    if (!error) toastTimer = setTimeout(hideToast, 3200);
  }
  async function refreshCart(sections) {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart refresh unavailable');
    const cart = await response.json();
    const drawer = document.querySelector('cart-drawer');
    const sectionId = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
    const markup = sections?.[sectionId];
    const parsed = markup && new DOMParser().parseFromString(markup, 'text/html');
    const updated = parsed?.querySelector('cart-drawer');
    // Impact consumes complete drawer markup on cart:change, even while closed.
    if (!drawer || (updated && (cart.item_count === 0 || (updated.querySelector('.cart-drawer__inner') && updated.querySelector('[slot="footer"]'))))) {
      cart.sections = sections || {};
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'pw:fragrance-add', cart } }));
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    return cart;
  }
  async function add(form, shop, formData, buttons, focusedButton, anchor, detail) {
    let accepted = false;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      if (sections.length) formData.set('sections', [...new Set(sections)].slice(0, 5).join(','));
      formData.set('sections_url', window.location.pathname);
      const response = await fetch(`${root()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        addFailure(shop, failure.description || shop.dataset.error, anchor, detail);
        return;
      }
      accepted = true;
      // The write succeeded: later parsing/refresh failures must never invite another add.
      const detailToClose = currentDetailFor(detail);
      if (detailToClose?.open) detailToClose.close();
      notify(shop, `${form.dataset.title || shop.dataset.productName} — ${shop.dataset.added}`, false, anchor);
      const result = await response.json();
      try {
        const cart = await refreshCart(result.sections);
        (form.isConnected ? form : shop).dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: {
          items: result.items || [result], cart, blockCartDrawerOpening: true
        } }));
      } catch (_) { document.dispatchEvent(new CustomEvent('cart:refresh')); }
    } catch (_) {
      if (!accepted) addFailure(shop, shop.dataset.uncertain, anchor, detail);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      buttons.forEach(({ button, disabled }) => { button.disabled = disabled; button.removeAttribute('aria-busy'); });
      pending.delete(form);
      if (detail) {
        pendingDetails.delete(formData.get('id'));
        const activeForm = activeDetail?.querySelector('[data-pw-detail-form]');
        if (activeForm?.elements.namedItem('id').value === formData.get('id')) {
          const button = activeForm.querySelector('[type="submit"]');
          button.disabled = button.dataset.available !== 'true';
          button.removeAttribute('aria-busy');
        }
      }
      if (focusedButton?.isConnected && document.activeElement === document.body) focusedButton.focus({ preventScroll: true });
    }
  }
  // Capture prevents native product-form from opening Impact's drawer before its event fires.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const detail = form.pwDetailContext;
    const shop = detail?.shop || document.querySelector('[data-pw-fragrance-shop]');
    if (!shop || (!form.matches('[data-pw-fragrance-form]') && form.getAttribute('id') !== shop.dataset.productForm)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending.has(form) || !form.reportValidity()) return;
    const data = new FormData(form);
    if (!data.get('id')) return;
    if (detail && pendingDetails.has(data.get('id'))) return;
    if (detail) {
      pendingDetails.add(data.get('id'));
      detail.dialog.querySelector('[data-pw-detail-error]').hidden = true;
    }
    pending.add(form);
    const focusedButton = document.activeElement?.form === form ? document.activeElement : null;
    const buttons = Array.from(form.elements).filter(el => el.type === 'submit').map(button => ({ button, disabled: button.disabled }));
    const anchor = detail?.anchor || event.submitter || buttons[0]?.button || form;
    buttons.forEach(({ button }) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    queue = queue.then(() => add(form, shop, data, buttons, focusedButton, anchor, detail));
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-pw-fragrance-open]');
    if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && openDetail(link)) event.preventDefault();
    const dismiss = event.target.closest('[data-pw-fragrance-dismiss]');
    if (dismiss) hideToast();
  });
  document.addEventListener('shopify:section:unload', () => { if (activeDetail?.open) activeDetail.close(); });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && activeToast) hideToast();
  });
})();
